import { Builder } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import borders from '../../storyblocks-design-system/design-tokens/borders';
import spacings from '../../storyblocks-design-system/design-tokens/spacings';
import { cn } from '../../storyblocks-design-system/utils';
import { generateFeatureCardsSchema } from '../../utils/structured-data/featuresCards.schema';
import LinkWrapper from '../elements/LinkWrapper/LinkWrapper';
import ProgressiveImage from '../elements/ProgressiveImage/ProgressiveImage';
import StructuredData from '../elements/StructuredData';
import BuilderSection from '../layouts/BuilderSection';

interface FeatureCards {
  image: string;
  imageAlt: string;
  title: string;
  body?: string;
  optionalText?: string;
  link?: string;
  price?: string;
  currency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'LimitedAvailability';
}

export type FeatureCardItem = Pick<
  FeatureCards,
  'title' | 'body' | 'image' | 'link' | 'price' | 'currency' | 'availability'
>;

export interface FeaturesCardsProps {
  heading: string;
  useBodyText?: boolean;
  bodyText: string;
  featureCards: FeatureCards[];
  backgroundColor?: string;
  cardBackgroundColor?: string;
  cardPadding?: string;
  cardBorderRadius?: string;
  enableMobileSwipe?: boolean;
  textColor?: string;
  cardTextColor?: string;
  optionalTextOpacity?: number;
  structuredDataType?:
    | 'product'
    | 'service'
    | 'article'
    | 'creative-work'
    | 'none';
  structuredDataUrl?: string;
  organizationName?: string;
}

export default function FeaturesCards({
  heading,
  useBodyText = false,
  bodyText,
  featureCards,
  backgroundColor,
  cardBackgroundColor,
  cardPadding,
  cardBorderRadius,
  enableMobileSwipe = false,
  textColor,
  cardTextColor,
  optionalTextOpacity = 0.8,
  structuredDataType = 'none',
  structuredDataUrl,
  organizationName = 'Storyblocks',
}: FeaturesCardsProps) {
  const containerClasses = enableMobileSwipe
    ? 'flex gap-8 overflow-x-auto pb-4 md:overflow-visible flex-nowrap md:flex-wrap'
    : 'flex gap-8 flex-wrap justify-center';

  const getCardSizes = () => {
    const cardCount = featureCards?.length || 0;
    const baseClasses = 'grow shrink-0 w-full max-w-md';

    if (cardCount === 2) {
      // 2 cards: 1 per row on mobile, 2 per row on md+
      return `${baseClasses} md:w-[calc(50%-16px)]`;
    } else if (cardCount === 3) {
      // 3 cards: 1 per row on mobile, 2 per row on md, 3 per row on lg+
      return `${baseClasses} md:w-[calc(50%-16px)] lg:w-[calc(33.333%-22px)]`;
    } else {
      // 4+ cards: Full responsive grid (1→2→3→4 per row)
      return `${baseClasses} md:w-[calc(50%-16px)] lg:w-[calc(25%-24px)]`;
    }
  };

  const getImageBorderRadius = () => {
    const defaultRadius = cardBorderRadius || 'var(--radius-lg, 1rem)';

    if (
      cardBackgroundColor &&
      backgroundColor &&
      cardBackgroundColor !== backgroundColor
    ) {
      return `${defaultRadius} ${defaultRadius} 0 0`;
    }

    return defaultRadius;
  };

  const structuredData = generateFeatureCardsSchema(
    structuredDataType as 'product' | 'service' | 'article' | 'creative-work',
    heading,
    featureCards?.map((card) => ({
      title: card.title,
      body: card.body,
      image: card.image,
      link: card.link,
      price: card.price,
      currency: card.currency,
      availability: card.availability,
    })) || [],
    {
      description: useBodyText ? bodyText : undefined,
      url: structuredDataUrl,
      organizationName,
    }
  );

  return (
    <div data-testid="features-cards-container" style={{ backgroundColor }}>
      {structuredData && <StructuredData data={structuredData} />}
      <BuilderSection>
        <div
          data-testid="features-cards-content"
          className="flex flex-col w-full"
        >
          <div
            className={twMerge(
              'flex flex-col items-center justify-center text-center',
              bodyText ? 'gap-4 mb-12' : 'gap-4 mb-8'
            )}
          >
            <Text
              asChild
              fontFamily="polysans"
              fontSize="4xl"
              className="font-bold"
            >
              <h2
                data-testid="features-cards-heading"
                className="m-0"
                style={{ color: textColor || '#111827' }}
              >
                {heading}
              </h2>
            </Text>

            {useBodyText && (
              <Text
                asChild
                fontSize="xl"
                fontWeight="regular"
                fontFamily="polysans"
                className="max-w-2xl mx-auto"
              >
                <div
                  data-testid="features-cards-body-text"
                  style={{ color: textColor || '#111827' }}
                  dangerouslySetInnerHTML={{ __html: bodyText }}
                />
              </Text>
            )}
          </div>

          <div data-testid="features-cards-grid" className={containerClasses}>
            {featureCards &&
              featureCards.length > 0 &&
              featureCards.map((card, index) => (
                <LinkWrapper
                  key={`feature-card-${index}`}
                  link={card.link}
                  className={cn(
                    'flex flex-col items-center',
                    enableMobileSwipe
                      ? 'flex-shrink-0 w-72 md:w-auto md:max-w-md'
                      : getCardSizes()
                  )}
                  style={{
                    backgroundColor: cardBackgroundColor,
                    borderRadius: cardBorderRadius || 'var(--radius-lg, 1rem)',
                  }}
                  data-testid={`feature-card-${index}`}
                  tabIndex={card.link ? 0 : -1}
                  role={card.link ? 'button' : undefined}
                  aria-label={
                    card.link ? `Navigate to ${card.title}` : undefined
                  }
                >
                  {card.image && (
                    <div
                      data-testid={`feature-card-image-container-${index}`}
                      className="w-full aspect-square overflow-hidden bg-white my-0"
                      style={{
                        borderRadius: getImageBorderRadius(),
                      }}
                    >
                      <ProgressiveImage
                        src={card.image}
                        alt={card.imageAlt}
                        className="w-full h-full object-cover"
                        width="288"
                        height="288"
                        shouldLazyLoad
                      />
                    </div>
                  )}
                  <div
                    className="flex flex-col gap-2"
                    style={{
                      padding: cardPadding,
                    }}
                  >
                    {card.optionalText && (
                      <p
                        data-testid={`feature-card-optional-text-${index}`}
                        className="my-0 font-medium text-xs"
                        style={{
                          color: cardTextColor || '#111827',
                          opacity: optionalTextOpacity,
                        }}
                      >
                        {card.optionalText}
                      </p>
                    )}
                    <Text
                      asChild
                      fontSize="2xl"
                      fontFamily="polysans"
                      className="text-2xl"
                    >
                      <h3
                        data-testid={`feature-card-title-${index}`}
                        className="mt-0 font-bold"
                        style={{ color: cardTextColor || '#111827' }}
                      >
                        {card.title}
                      </h3>
                    </Text>

                    {!!card.body?.trim() && (
                      <Text
                        asChild
                        fontSize="lg"
                        fontWeight="regular"
                        fontFamily="polysans"
                        className="text-lg"
                      >
                        <div
                          data-testid={`feature-card-body-${index}`}
                          style={{ color: cardTextColor || '#111827' }}
                          dangerouslySetInnerHTML={{ __html: card.body }}
                        />
                      </Text>
                    )}
                  </div>
                </LinkWrapper>
              ))}
          </div>
        </div>
      </BuilderSection>
    </div>
  );
}

Builder.registerComponent(FeaturesCards, {
  name: 'Features Cards',
  inputs: [
    {
      name: 'heading',
      type: 'string',
      required: true,
      defaultValue: 'The Storyblocks difference',
    },
    {
      name: 'useBodyText',
      friendlyName: 'Toggle Body Text',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Toggle to use body text for the section',
    },
    {
      name: 'bodyText',
      friendlyName: 'Body Text',
      type: 'richText',
      showIf: 'options.get("useBodyText")',
      required: false,
      defaultValue:
        'Optional body copy here: Using stock footage saves video producers time and money, and our library offers a nearly endless supply of content.',
      onChange: (options) => {
        const characterLimit = 300;
        const bodyText = options.get('bodyText');
        const htmlTagRegex = /<.*?>/gi;

        const textOnly = bodyText.replaceAll(htmlTagRegex, '');
        if (textOnly.length <= characterLimit) {
          return;
        }

        const tokenizedTags = [...bodyText.matchAll(htmlTagRegex)].map(
          (item) => item[0]
        );
        const tokenizedText = bodyText.split(htmlTagRegex);

        const trimmedTextNodes = tokenizedText.reduce((textNodes, text) => {
          const currentTextLength = textNodes.join('').length;
          return currentTextLength + text.length <= characterLimit
            ? textNodes.concat(text)
            : textNodes.concat(
                text.slice(0, characterLimit - currentTextLength)
              );
        }, []);

        const trimmedRichText = tokenizedTags
          .map((tag, i) => trimmedTextNodes[i] + tag)
          .join('');

        alert(`Max of ${characterLimit} characters allowed.`);
        options.set('bodyText', trimmedRichText);
      },
    },
    {
      name: 'featureCards',
      friendlyName: 'Feature Cards',
      type: 'list',
      required: true,
      helperText:
        'A list of feature cards highlighting key differentiators. Each card includes an image, title, and description.',
      defaultValue: [
        {
          image: '',
          imageAlt: 'High quality diverse stock library',
          title: 'High-quality, diverse stock library',
          body: 'Our library is expertly curated to provide the in-demand content you need to make your creative stand out.',
          optionalText: '',
          link: '',
          price: '',
          currency: 'USD',
          availability: 'InStock',
        },
        {
          image: '',
          imageAlt: 'Premium quality assets',
          title: 'Premium Quality Assets',
          body: 'Access professionally produced content that meets the highest standards of quality and creativity.',
          optionalText: '',
          link: '',
          price: '',
          currency: 'USD',
          availability: 'InStock',
        },
        {
          image: '',
          imageAlt: 'Unlimited downloads',
          title: 'Unlimited Downloads',
          body: 'Download as many assets as you need without worrying about additional costs or limitations.',
          optionalText: '',
          link: '',
          price: '',
          currency: 'USD',
          availability: 'InStock',
        },
        {
          image: '',
          imageAlt: 'Commercial license',
          title: 'Commercial License',
          body: 'Use our content for any commercial project with our comprehensive licensing agreement.',
          optionalText: '',
          link: '',
          price: '',
          currency: 'USD',
          availability: 'InStock',
        },
      ],
      onChange: (options) => {
        const maxAllowed = 8;

        if (options.get('featureCards').length > maxAllowed) {
          options.set(
            'featureCards',
            options.get('featureCards').slice(0, maxAllowed)
          );
          alert(
            `Maximum number of feature cards allowed is ${maxAllowed}. Modify an existing feature card instead.`
          );
        }
      },
      subFields: [
        {
          name: 'image',
          friendlyName: 'Image',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
          required: false,
          helperText: 'Upload an image for the feature card',
        },
        {
          name: 'imageAlt',
          friendlyName: 'Image Alt Text',
          type: 'string',
          required: true,
          helperText: 'Descriptive text for screen readers and accessibility',
        },
        {
          name: 'optionalText',
          friendlyName: 'Optional Text',
          type: 'string',
          required: false,
          helperText: 'Optional text that appears between image and card title',
        },
        {
          name: 'title',
          friendlyName: 'Card Title',
          type: 'string',
          required: true,
        },
        {
          name: 'body',
          friendlyName: 'Card Body',
          type: 'richText',
          required: false,
          helperText: 'Description text for the feature card',
        },
        {
          name: 'link',
          friendlyName: 'Card Link',
          type: 'string',
          required: false,
          helperText:
            'Optional link URL for the entire card (internal or external)',
        },
        {
          name: 'price',
          friendlyName: 'Price',
          type: 'string',
          required: false,
          helperText:
            'Price for product/service (e.g., "49.99"). Required for product/service structured data.',
          showIf:
            'options.get("structuredDataType") === "product" || options.get("structuredDataType") === "service"',
        },
        {
          name: 'currency',
          friendlyName: 'Currency',
          type: 'string',
          required: false,
          defaultValue: 'USD',
          helperText:
            'Currency code (e.g., USD, EUR). Used with price for structured data.',
          showIf:
            'options.get("structuredDataType") === "product" || options.get("structuredDataType") === "service"',
        },
        {
          name: 'availability',
          friendlyName: 'Availability',
          type: 'string',
          enum: [
            { label: 'In Stock', value: 'InStock' },
            { label: 'Out of Stock', value: 'OutOfStock' },
            { label: 'Pre-Order', value: 'PreOrder' },
            { label: 'Limited Availability', value: 'LimitedAvailability' },
          ],
          defaultValue: 'InStock',
          required: false,
          helperText: 'Product availability status for structured data',
          showIf: 'options.get("structuredDataType") === "product"',
        },
      ],
    },

    {
      name: 'backgroundColor',
      friendlyName: 'Background Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Select a custom background color for the section',
    },
    {
      name: 'cardBackgroundColor',
      friendlyName: 'Card Background Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Select a custom background color for the cards',
    },
    {
      name: 'cardPadding',
      friendlyName: 'Card Padding',
      type: 'string',
      defaultValue: '',
      helperText: 'Select padding value from design system tokens',
      enum: spacings.map((spacing) => ({
        label: spacing.name.toUpperCase(),
        value: spacing.value,
      })),
    },
    {
      name: 'cardBorderRadius',
      friendlyName: 'Card Border Radius',
      type: 'string',
      defaultValue: 'var(--radius-lg, 1rem)',
      helperText: 'Select border radius value from design system tokens',
      enum: borders.map((border) => ({
        label: border.name,
        value: border.value,
      })),
    },
    {
      name: 'enableMobileSwipe',
      friendlyName: 'Enable Mobile Swipe',
      type: 'boolean',
      defaultValue: false,
      helperText:
        'Allow users to swipe horizontally through cards on mobile devices',
    },
    {
      name: 'textColor',
      friendlyName: 'Text Color',
      type: 'color',
      defaultValue: '',
      helperText: 'Select text color for section heading and body text',
    },
    {
      name: 'cardTextColor',
      friendlyName: 'Card Text Color',
      type: 'color',
      defaultValue: '',
      helperText:
        'Select text color for all text elements inside cards (titles, descriptions, optional text)',
    },
    {
      name: 'optionalTextOpacity',
      friendlyName: 'Optional Text Opacity',
      type: 'number',
      defaultValue: 0.8,
      min: 0,
      max: 1,
      step: 0.1,
      helperText:
        'Set the opacity level for optional text in cards (0 = transparent, 1 = fully opaque)',
    },

    {
      name: 'structuredDataType',
      friendlyName: 'Structured Data Type',
      type: 'string',
      enum: [
        { label: 'None (No structured data)', value: 'none' },
        { label: 'Product (E-commerce items)', value: 'product' },
        { label: 'Service (Service offerings)', value: 'service' },
        { label: 'Article (Blog posts, news)', value: 'article' },
        { label: 'Creative Work (General content)', value: 'creative-work' },
      ],
      defaultValue: 'none',
      helperText:
        'Select the type of structured data to generate for improved SEO and search visibility',
    },
    {
      name: 'structuredDataUrl',
      friendlyName: 'Structured Data URL',
      type: 'string',
      required: false,
      helperText:
        'Optional canonical URL for the structured data (improves SEO context)',
      showIf: 'options.get("structuredDataType") !== "none"',
    },
    {
      name: 'organizationName',
      friendlyName: 'Organization Name',
      type: 'string',
      defaultValue: 'Storyblocks',
      required: false,
      helperText:
        'Organization name for structured data context (use "Storyblocks" for automatic branding)',
      showIf: 'options.get("structuredDataType") !== "none"',
    },
    {
      name: 'Identifier',
      type: 'string',
      enum: ['Stockblocks'],
      defaultValue: 'Stockblocks',
      advanced: true,
      onChange: (_, context) => {
        context.set('Identifier', 'Stockblocks');
      },
      helperText: "Identifier for the repo, don't change this",
    },
  ],
});
